// The signed-in layout: top bar (organisation switcher, navigation, live indicator, user menu) and the
// banners that must stay visible (impersonation, unverified address, billing, pending erasure).
//
// Read-only (the kiosk host, or a kiosk, lib/kiosk.ts): everyone acts as a viewer, so the pages hide their edit
// controls; the menu keeps the data pages and exports, other pages redirect to the overview, and data refreshes by
// polling (a kiosk has no live connection).

import type { Impersonation, MemberRole, Organisation } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { setLanguage, useI18n } from '../i18n/index.ts';
import { api, ApiError, onUnauthorized } from '../lib/api.ts';
import { formatDate, formatTime } from '../lib/format.ts';
import { queuedFor, useHaccpSyncEngine } from '../lib/haccpSync.ts';
import { type LiveState, useLive } from '../lib/live.ts';
import { KIOSK_REFRESH_MS, kioskHost, READ_ONLY_PAGES } from '../lib/kiosk.ts';
import {
  type DashboardUser, keys, useAlarms, useBilling, useHaccpSettings, useLoraBases, useMe, useOrg, useSelfSignUp,
} from '../lib/queries.ts';
import { ReauthDialog } from '../lib/reauth.tsx';
import { Tour } from './Tour.tsx';
import { Alert, Button, cx, LinkButton, Logo, Spinner, ThemeToggle } from './ui.tsx';

// ---------------------------------------------------------------- the current organisation

export interface OrgContextValue {
  orgId: string;
  org: Organisation;
  role: MemberRole;
  can: (min: MemberRole) => boolean;
}

const OrgContext = createContext<OrgContextValue | null>(null);

// The signed-in user, provided by RequireAuth. Pages read it from here rather than from the `me` query:
// when the session ends (sign-out elsewhere, 401, live socket closed with 4401) the query turns to null,
// and RequireAuth then redirects before anything below it renders without a user.
const UserContext = createContext<DashboardUser | null>(null);

export function useUser(): DashboardUser {
  const u = useContext(UserContext);
  if (!u) throw new Error('useUser outside RequireAuth');
  return u;
}

// As ROLE_RANK in @cck/schema: the dashboard imports only types from there, as its zod schemas would
// more than double the size of the bundle.
const ROLE_RANK: Record<MemberRole, number> = { viewer: 1, operator: 2, admin: 3, owner: 4 };

export function useCurrentOrg(): OrgContextValue {
  const v = useContext(OrgContext);
  if (!v) throw new Error('useCurrentOrg outside an organisation');
  return v;
}

/** Whether this dashboard is read-only: the kiosk host, or a kiosk. */
export const useReadOnly = (): boolean => kioskHost || !!useContext(UserContext)?.kiosk;

const LAST_ORG = 'cck.lastOrg';

export function lastOrg(): string | null {
  try {
    return localStorage.getItem(LAST_ORG);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- layouts

/** Pages that need a session: signed-out users go to the sign-in page, and come back afterwards. */
export function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useI18n();

  useEffect(() => {
    onUnauthorized(() => {
      // A kiosk whose link was revoked has nobody to sign in: it says so.
      const kiosk = !!qc.getQueryData<DashboardUser | null>(keys.me)?.kiosk;
      qc.setQueryData(keys.me, null);
      navigate(kiosk ? '/k' : `/auth/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`);
    });
    return () => onUnauthorized(null);
  }, [qc, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (me.data) setLanguage(me.data.locale);
  }, [me.data]);

  const readOnly = kioskHost || !!me.data?.kiosk;
  useEffect(() => {
    if (!readOnly) return;
    document.documentElement.classList.add('kiosk');
    qc.setDefaultOptions({ queries: { ...qc.getDefaultOptions().queries, refetchInterval: KIOSK_REFRESH_MS } });
  }, [readOnly, qc]);

  if (me.isPending) return <FullPage><Spinner label={t('common.loading')} /></FullPage>;
  // A failed refresh keeps the user known (a phone that lost the network keeps its pages, HACCP records included).
  if (me.isError && !me.data) return <FullPage><Alert tone="red">{t('errors.unavailable')}</Alert></FullPage>;
  if (!me.data) return <Navigate to={`/auth/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return (
    <UserContext.Provider value={me.data}>
      <ReauthDialog />
      <Outlet />
    </UserContext.Provider>
  );
}

export function FullPage({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>;
}

/** /o/:orgId — the organisation's pages, with live updates while open. */
export function OrgLayout() {
  const { orgId = '' } = useParams();
  const me = useUser();
  const org = useOrg(orgId);
  const { t } = useI18n();
  const membership = me.organisations.find((o) => o.org_id === orgId);

  useEffect(() => {
    try {
      localStorage.setItem(LAST_ORG, orgId);
    } catch {
      // storage unavailable
    }
  }, [orgId]);

  const readOnly = useReadOnly();
  const location = useLocation();
  if (!membership || (org.error instanceof ApiError && org.error.status === 404)) return <Navigate to="/" replace />;
  if (readOnly && !READ_ONLY_PAGES.test(location.pathname)) return <Navigate to={`/o/${orgId}`} replace />;
  if (org.error instanceof ApiError && org.error.code === 'two_factor_setup_required') {
    return (
      <Chrome orgId={orgId} live="offline">
        <Alert tone="amber" title={t('shell.twoFactorRequired')}
          action={<LinkButton to="/profile#security" size="sm">{t('shell.enableTwoFactor')}</LinkButton>}>
          {t('shell.twoFactorRequiredBody', { org: membership.name })}
        </Alert>
      </Chrome>
    );
  }
  if (org.isPending) return <FullPage><Spinner label={t('common.loading')} /></FullPage>;
  if (org.isError && !org.data) return <FullPage><Alert tone="red">{t('errors.unavailable')}</Alert></FullPage>;
  const role = readOnly ? 'viewer' : membership.role;
  const value: OrgContextValue = { orgId, org: org.data, role, can: (min) => ROLE_RANK[role] >= ROLE_RANK[min] };
  return (
    <OrgContext.Provider value={value}>
      <LiveChrome orgId={orgId}>
        <OrgBanners org={org.data} role={role} />
        <Outlet />
      </LiveChrome>
    </OrgContext.Provider>
  );
}

function LiveChrome({ orgId, children }: { orgId: string; children: React.ReactNode }) {
  const me = useUser();
  const live = useLive(orgId, !me.kiosk);
  useHaccpSyncEngine(me.id, me.organisations.map((o) => o.org_id));
  return <Chrome orgId={orgId} live={live}>{children}</Chrome>;
}

/** A trial reminder is shown to Owners in the last week. */
const TRIAL_REMINDER_MS = 7 * 86_400_000;

function OrgBanners({ org, role }: { org: Organisation; role: MemberRole }) {
  const { t, lang } = useI18n();
  const owner = role === 'owner';
  const billing = useBilling(org.id, owner && org.billing_state !== 'active').data;
  const toBilling = owner && (
    <LinkButton to={`/o/${org.id}/settings/billing`} size="sm" variant="secondary">{t('shell.billingAction')}</LinkButton>
  );
  const trialEnd = billing && !billing.subscribed && billing.trial_end ? Date.parse(billing.trial_end) : null;
  return (
    <div className="mb-4 flex flex-col gap-2 empty:hidden">
      {org.billing_state === 'trialing' && trialEnd !== null && trialEnd - Date.now() < TRIAL_REMINDER_MS && (
        <Alert tone="blue" action={toBilling}>{t('shell.trialEnds', { date: formatDate(billing!.trial_end, lang) })}</Alert>
      )}
      {org.billing_state === 'restricted' && (
        <Alert tone="red" title={t('shell.restricted')} action={toBilling}>
          {owner ? t('shell.restrictedOwner') : t('shell.restrictedMember')}</Alert>
      )}
      {org.billing_state === 'canceled' && (
        <Alert tone="red" title={t('shell.canceled')} action={toBilling}>
          {owner ? t('shell.canceledOwner', { date: formatDate(billing?.ingestion_until, lang) }) : t('shell.canceledMember')}
        </Alert>
      )}
      {org.billing_state === 'past_due' && owner && (
        <Alert tone="amber" action={toBilling}>{billing?.provider === 'revolut' ? t('shell.pastDueRevolut') : t('shell.pastDue')}</Alert>
      )}
      {org.deletion_requested_at && (
        <Alert tone="red" title={t('shell.erasurePending', { date: formatDate(new Date(Date.parse(org.deletion_requested_at)
          + 7 * 86_400_000).toISOString(), lang) })}>
          {role === 'owner' && <Link className="underline" to="settings/organisation">{t('shell.erasureCancel')}</Link>}
        </Alert>
      )}
    </div>
  );
}

/** Signed-in pages outside an organisation (profile, onboarding). */
export function PlainLayout() {
  if (useReadOnly()) return <Navigate to="/" replace />;
  return <Chrome orgId={null} live={null}><Outlet /></Chrome>;
}

// ---------------------------------------------------------------- the top bar

function Chrome({ orgId, live, children }: { orgId: string | null; live: LiveState | null; children: React.ReactNode }) {
  const me = useUser();
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const selfSignUp = useSelfSignUp();
  const readOnly = useReadOnly();
  const location = useLocation();
  // The guided tour runs on the overview, opened by navigating there with { tour: true } (Overview.tsx, the menu).
  const tourOrg = orgId ?? lastOrg() ?? me.organisations[0]?.org_id;
  const touring = !!orgId && (location.state as { tour?: boolean } | null)?.tour === true
    && location.pathname.replace(/\/$/, '') === `/o/${orgId}`;
  const admin = !readOnly && !!orgId && ROLE_RANK[me.organisations.find((o) => o.org_id === orgId)?.role ?? 'viewer'] >= ROLE_RANK.admin;

  const signOut = async () => {
    // HACCP records not sent yet stay on this phone, for this user only (lib/haccpSync.ts): say so before leaving.
    const queued = queuedFor(me.id).filter((r) => r.state === 'pending').length;
    if (queued > 0 && !confirm(t('haccpSync.signOutConfirm', { count: queued }))) return;
    await api('/auth/sign-out', { method: 'POST' }).catch(() => undefined);
    qc.clear();
    navigate('/auth/sign-in');
  };

  return (
    <div className="min-h-screen">
      {me.impersonation && <SupportBanner impersonation={me.impersonation} name={me.display_name || me.email} />}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
          <Link to={orgId ? `/o/${orgId}` : '/'} aria-label="Maillon Froid" className="flex items-center text-[17px]">
            <Logo wordmark={<span className="hidden sm:inline">Maillon Froid</span>} />
          </Link>
          {orgId && <OrgSwitcher orgId={orgId} />}
          {orgId && <MainNav orgId={orgId} />}
          <div className="ml-auto flex items-center gap-3">
            {live && !me.kiosk && <LiveDot state={live} />}
            <ThemeToggle labels={{ light: t('shell.themeLight'), dark: t('shell.themeDark') }} />
            {me.kiosk ? <KioskLabel name={me.kiosk.site_name ? `${me.kiosk.name} · ${me.kiosk.site_name}` : me.kiosk.name} /> : (
            <div className="relative">
              <button type="button" className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-slate-100"
                data-tour="user-menu" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                  {(me.display_name || me.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden max-w-40 truncate md:inline">{me.display_name || me.email}</span>
              </button>
              {menu && (
                <div role="menu" className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
                  onClick={() => setMenu(false)}>
                  {!readOnly && <Link role="menuitem" to="/profile" className="block px-3 py-2 text-sm hover:bg-slate-50">{t('shell.profile')}</Link>}
                  {selfSignUp && !readOnly && (
                    <Link role="menuitem" to="/onboarding" className="block px-3 py-2 text-sm hover:bg-slate-50">{t('shell.newOrg')}</Link>
                  )}
                  {tourOrg && (
                    <Link role="menuitem" to={`/o/${tourOrg}`} state={{ tour: true }} className="block px-3 py-2 text-sm hover:bg-slate-50">
                      {t('tour.start')}</Link>
                  )}
                  {admin && <Link role="menuitem" to={`/o/${orgId}/setup`} className="block px-3 py-2 text-sm hover:bg-slate-50">{t('tour.setup')}</Link>}
                  <button role="menuitem" type="button" onClick={signOut}
                    className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-slate-50">{t('shell.signOut')}</button>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </header>
      {readOnly && !me.kiosk && (
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-1 text-center text-xs text-slate-500">{t('kiosk.readOnly')}</div>
      )}
      {!me.email_verified && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900">
          {t('shell.verifyEmail', { email: me.email })}
        </div>
      )}
      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
      {touring && <Tour admin={admin} onClose={() => navigate(location, { replace: true, state: null })} />}
    </div>
  );
}

/**
 * A support session (platform staff signed in as this member, docs/architecture.md §12): visible on every
 * page, with its mode and end, and a way to end it now. At its end, the session ends here too.
 */
function SupportBanner({ impersonation, name }: { impersonation: Impersonation; name: string }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const end = async () => {
    setBusy(true);
    await api('/auth/sign-out', { method: 'POST' }).catch(() => undefined);
    qc.clear();
    navigate('/auth/support-ended', { replace: true });
  };
  useEffect(() => {
    const ms = Date.parse(impersonation.expires_at) - Date.now();
    const timer = setTimeout(() => {
      qc.clear();
      navigate('/auth/support-ended', { replace: true });
    }, Math.max(0, Math.min(ms, 2 ** 31 - 1)));
    return () => clearTimeout(timer);
  }, [impersonation.expires_at, qc, navigate]);
  const time = formatTime(impersonation.expires_at, lang);
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-violet-700 px-4 py-1.5 text-sm text-on-color">
      <span>{t(impersonation.mode === 'write' ? 'shell.supportWrite' : 'shell.supportReadOnly', { name, time })}</span>
      <Button size="sm" variant="secondary" loading={busy} onClick={() => void end()}>{t('shell.endSupport')}</Button>
    </div>
  );
}

function OrgSwitcher({ orgId }: { orgId: string }) {
  const me = useUser();
  const navigate = useNavigate();
  const { t } = useI18n();
  if (me.organisations.length < 2) {
    return <span data-tour="org" className="max-w-48 truncate text-sm font-medium text-slate-700">{me.organisations.find((o) => o.org_id === orgId)?.name}</span>;
  }
  return (
    <select data-tour="org" aria-label={t('shell.organisation')} value={orgId} onChange={(e) => navigate(`/o/${e.target.value}`)}
      className="max-w-56 rounded-md border border-slate-300 bg-white py-1 pl-2 pr-7 text-sm">
      {me.organisations.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
    </select>
  );
}

function MainNav({ orgId }: { orgId: string }) {
  const { t } = useI18n();
  const readOnly = useReadOnly();
  // Open alarms (in progress or awaiting acknowledgement), counted up to 9.
  const open = useAlarms(orgId, 'open=true', 10).data;
  const count = open ? (open.data.length > 9 || open.next_cursor ? '9+' : open.data.length || null) : null;
  // HACCP records: shown when the plan has at least one of their modules.
  const haccp = useHaccpSettings(orgId).data?.features;
  const hasHaccp = haccp ? haccp.cooling || haccp.reception || haccp.cleaning : false;
  // LoRa bases: shown once the organisation has one (the Devices page links to their onboarding).
  const hasLora = (useLoraBases(orgId).data?.data.length ?? 0) > 0;
  const item = (to: string, label: string, tour: string, end = false, badge: string | number | null = null) => (
    <NavLink to={to} end={end} data-tour={`nav-${tour}`} className={({ isActive }) => cx('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
      isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')}>
      {label}
      {badge ? <span className="rounded-full bg-red-600 px-1.5 text-xs font-semibold text-on-color">{badge}</span> : null}
    </NavLink>
  );
  return (
    <nav aria-label={t('shell.navigation')} className="order-last flex w-full gap-1 overflow-x-auto md:order-none md:w-auto">
      {item(`/o/${orgId}`, t('nav.overview'), 'overview', true)}
      {item(`/o/${orgId}/alarms`, t('nav.alarms'), 'alarms', false, count)}
      {!readOnly && item(`/o/${orgId}/devices`, t('nav.devices'), 'devices')}
      {hasLora && !readOnly && item(`/o/${orgId}/lora`, t('nav.lora'), 'lora')}
      {hasHaccp && item(`/o/${orgId}/haccp`, t('nav.haccp'), 'haccp')}
      {!readOnly && item(`/o/${orgId}/maintenance`, t('nav.maintenance'), 'maintenance')}
      {item(`/o/${orgId}/energy`, t('nav.energy'), 'energy')}
      {readOnly ? item(`/o/${orgId}/exports`, t('nav.exports'), 'exports') : item(`/o/${orgId}/settings`, t('nav.settings'), 'settings')}
    </nav>
  );
}

/** A kiosk's discreet label: its link's name, and a way off kiosk mode for whoever set the screen up. */
function KioskLabel({ name }: { name: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const leave = async () => {
    if (!confirm(t('kiosk.leaveConfirm'))) return;
    await api('/auth/kiosk', { method: 'DELETE' }).catch(() => undefined);
    qc.clear();
    navigate('/auth/sign-in');
  };
  return (
    <span className="flex items-center gap-2 text-xs text-slate-500">
      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium uppercase tracking-wide">{t('kiosk.label')}</span>
      <span className="hidden max-w-48 truncate sm:inline">{name}</span>
      <button type="button" onClick={() => void leave()} className="underline-offset-2 hover:underline">{t('kiosk.leave')}</button>
    </span>
  );
}

function LiveDot({ state }: { state: LiveState }) {
  const { t } = useI18n();
  return (
    <span data-tour="live" className="flex items-center gap-1.5 text-xs text-slate-500" title={t(`live.${state}`)}>
      <span className={cx('h-2 w-2 rounded-full', state === 'live' ? 'bg-emerald-500' : state === 'connecting' ? 'bg-amber-400' : 'bg-slate-400')} />
      <span className="hidden lg:inline">{t(`live.${state}`)}</span>
    </span>
  );
}
